import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PdfDocumentViewer, type PdfViewerPersistence } from '@liscio/ui';

import PDFViewerErrorBoundary from 'common/PDFViewerErrorBoundary/PDFViewerErrorBoundary';
import { reportPdfViewerError } from 'common/PDFViewerErrorBoundary/reportPdfViewerError';
import { useGetUserFromLocalStorage } from 'hooks/useGetUserFromLocalStorage';
import { useDocumentAnnotationsSource } from 'hooks/document-annotations-hooks';
import { useDocumentRotationSource } from 'hooks/document-rotation-hooks';

import AnnotationVisibilityNotice from './AnnotationVisibilityNotice';
import {
  DOWNLOAD_REQUESTED_EVENT,
  type DownloadRequestedDetail,
} from './pdfDownloadEvents';

export type SharedDocumentPDFViewerProps = {
  url: string;
  fileName: string;
  downloadUrl?: string;
  documentUuid?: string | null;
  isRenaming?: boolean;
  /** Displays stored annotations and rotation without editing tools or saves. */
  readOnly?: boolean;
};

const wrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'inherit',
};

const viewerSlotStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  height: '100%',
};

/** Legacy Files adapter: firm-only persistence, download coordination, and onboarding notice. */
export default function SharedDocumentPDFViewer({
  url,
  fileName,
  downloadUrl,
  documentUuid,
  isRenaming = false,
  readOnly = false,
}: SharedDocumentPDFViewerProps) {
  const { storedUser } = useGetUserFromLocalStorage();
  const isEmployee = storedUser?.is_employee === true;
  const canEdit =
    isEmployee &&
    storedUser?.userpilot_data?.cpa_license_tier?.toLowerCase() === 'pro';
  const firmDocumentUuid = isEmployee ? documentUuid : undefined;
  const annotationsSource = useDocumentAnnotationsSource(firmDocumentUuid);
  const rotationSource = useDocumentRotationSource(firmDocumentUuid);
  const persistence = useMemo<PdfViewerPersistence | undefined>(
    () =>
      annotationsSource && rotationSource
        ? {
            load: async () => {
              const [instantJson, rotation] = await Promise.all([
                annotationsSource.load(),
                rotationSource.load(),
              ]);
              return { instantJson, rotation };
            },
            saveAnnotations: annotationsSource.save,
            saveRotation: rotationSource.save,
          }
        : undefined,
    [annotationsSource, rotationSource],
  );

  const [showNotice, setShowNotice] = useState(false);
  const [downloadRequestToken, setDownloadRequestToken] = useState(0);
  const isRenamingRef = useRef(isRenaming);
  isRenamingRef.current = isRenaming;
  // A request made while the viewer chunk loads would be lost, so it stays
  // unclaimed and DocumentShow downloads directly.
  const viewerMountedRef = useRef(false);
  const handleViewerMountChange = useCallback((mounted: boolean) => {
    viewerMountedRef.current = mounted;
  }, []);

  useEffect(() => {
    const handleDownloadRequest = (event: Event) => {
      const { detail } = event as CustomEvent<DownloadRequestedDetail>;
      if (
        !viewerMountedRef.current ||
        isRenamingRef.current ||
        !detail?.originalUrl ||
        detail.originalUrl !== (downloadUrl || url)
      )
        return;
      event.preventDefault();
      setDownloadRequestToken((value) => value + 1);
    };
    window.addEventListener(DOWNLOAD_REQUESTED_EVENT, handleDownloadRequest);
    return () =>
      window.removeEventListener(DOWNLOAD_REQUESTED_EVENT, handleDownloadRequest);
  }, [downloadUrl, url]);

  const handleFirstAnnotationCreate = useCallback(() => setShowNotice(true), []);
  const handleDismissNotice = useCallback(() => setShowNotice(false), []);

  return (
    <div style={wrapperStyle}>
      <AnnotationVisibilityNotice
        visible={showNotice}
        onDismiss={handleDismissNotice}
      />
      <div style={viewerSlotStyle}>
        <PDFViewerErrorBoundary key={url} operation="loadPdfDocumentViewer">
          <PdfDocumentViewer
            documentId={documentUuid || url}
            url={url}
            title={fileName}
            downloadUrl={downloadUrl}
            downloadDisabled={isRenaming}
            downloadRequestToken={downloadRequestToken}
            persistence={persistence}
            authorName={storedUser?.uname}
            notesDescription="Annotations are saved for your firm only — clients don’t see them."
            canAnnotate={!!firmDocumentUuid && canEdit && !readOnly}
            canRotate={!!firmDocumentUuid && !readOnly}
            onFirstAnnotationCreate={handleFirstAnnotationCreate}
            onViewerMountChange={handleViewerMountChange}
            onError={reportPdfViewerError}
          />
        </PDFViewerErrorBoundary>
      </div>
    </div>
  );
}
